/**
 * Form Submit Tracking Handler
 *
 * Trackt Form-Submit-Events und sendet sie als "form" Events an den DataLayer.
 * Liest success/error Status aus <dl-submit> Custom Element aus.
 *
 * @example
 * <div component="kontaktformular">
 *   <form>
 *     <input type="text" name="name">
 *     <dl-submit success="Erfolgreich gesendet"></dl-submit>
 *     <button type="submit">Absenden</button>
 *   </form>
 * </div>
 *
 * Output:
 * {
 *   event: "form",
 *   component: "kontaktformular",
 *   success: "Erfolgreich gesendet"
 * }
 *
 * @see {@link ../../docs/03-tracking/non-ecommerce-events.md#form}
 * @see {@link ../../docs/02-apis/custom-elements.md#dl-submit}
 */
import DataLayerService from "../../../DataLayerService.js";
import { collectComponentAttributes } from "../../common/component-attributes.js";
import waitForElement from "../../../../../helper/waitForElement.js";
export { createFormParams };

const datalayerService = new DataLayerService();

document.addEventListener("submit", submitHandler);

/**
 * Behandelt Submit Events für Forms
 * Sucht nach <dl-submit> Element und liest success/error Attribute aus
 *
 * @param {Event} event - Submit Event
 */
export async function submitHandler(event) {
	const { target } = event;
    const params = await createFormParams(target);
    datalayerService.pushEvent("form", params);
}

/**
 * Erstellt die Parameter für das form Event
 *
 * @param {HTMLFormElement} form - Das Form-Element
 * @returns {Promise<Object>} Parameter-Objekt für das form Event
 */
async function createFormParams(form) {
    const params = {};

    // Warte auf <dl-submit> Element innerhalb des Forms (falls es dynamisch geladen wird)
    const dlSubmitElement = await waitForElement(form, "dl-submit", 200);

    if (dlSubmitElement) {
        // Lese success und error Attribute aus
        const success = dlSubmitElement.getAttribute("success");
        const error = dlSubmitElement.getAttribute("error");

        if (error) {
            params.error = error;
        } else if (success) {
            params.success = success;
        } else {
            params.success = "erfolgreich";
        }
    } else {
        // Fallback wenn kein dl-submit Element vorhanden
        params.success = "erfolgreich";
    }

    // component
    const componentElement = form.closest("[component]");
    if (componentElement) {
        params.component = componentElement.getAttribute("component");
    }

    // list (von product-list)
    const productListElement = form.closest("[product-list]");
    if (productListElement) {
        params.list = productListElement.getAttribute("product-list");
    }

    // Zusätzliche Attribute vom component-Element
    const componentAttributes = collectComponentAttributes(componentElement);
    Object.assign(params, componentAttributes);

    return params;
}
